<!-- eslint-disable prettier/prettier -->
<template>
  <section class="our-team" id="our-team">
    <div class="container">
      <div class="row">
        <hr class="team-divider" />
      </div>
    </div>
    <div class="team-shell">
      <div class="team-header">
        <h2>{{ teamHeading }}</h2>
        <p>{{ teamIntro }}</p>
      </div>

      <div class="team-timeline" v-if="teamRows.length">
        <div class="timeline-line" aria-hidden="true"></div>

        <div class="team-row" v-for="(row, index) in teamRows" :key="index">
          <article
            class="team-card team-card-left"
            :class="{ 'team-card-featured': index === 0 }"
            v-if="row.left"
          >
            <div class="member-avatar">
              <img
                v-if="getMemberImageUrl(row.left)"
                :src="getMemberImageUrl(row.left)"
                :alt="row.left.Name"
              />
              <span v-else class="avatar-fallback" aria-hidden="true"></span>
            </div>
            <div class="member-copy">
              <h3>{{ row.left.Name }}</h3>
              <p>{{ row.left.Position }}</p>
              <span>{{ getDepartment(row.left) }}</span>
            </div>
            <a
              class="linkedin-link"
              :class="{ 'linkedin-link-muted': !getLinkedInUrl(row.left) }"
              :href="getLinkedInUrl(row.left) || '#'"
              target="_blank"
              rel="noopener noreferrer"
              :aria-label="`${row.left.Name} LinkedIn`"
              @click="handleLinkedInClick($event, row.left)"
            >
              <img src="../../assets/img/linkedin-blue.png" alt="" />
            </a>
          </article>
          <div class="team-card-placeholder" v-else></div>

          <article class="team-card team-card-right" v-if="row.right">
            <div class="member-avatar">
              <img
                v-if="getMemberImageUrl(row.right)"
                :src="getMemberImageUrl(row.right)"
                :alt="row.right.Name"
              />
              <span v-else class="avatar-fallback" aria-hidden="true"></span>
            </div>
            <div class="member-copy">
              <h3>{{ row.right.Name }}</h3>
              <p>{{ row.right.Position }}</p>
              <span>{{ getDepartment(row.right) }}</span>
            </div>
            <a
              class="linkedin-link"
              :class="{ 'linkedin-link-muted': !getLinkedInUrl(row.right) }"
              :href="getLinkedInUrl(row.right) || '#'"
              target="_blank"
              rel="noopener noreferrer"
              :aria-label="`${row.right.Name} LinkedIn`"
              @click="handleLinkedInClick($event, row.right)"
            >
              <img src="../../assets/img/linkedin-blue.png" alt="" />
            </a>
          </article>
          <div class="team-card-placeholder" v-else></div>
        </div>
      </div>

      <div class="team-bottom">
        <p>{{ teamDescription }}</p>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  computed: {
    members() {
      return this.$store.state.dataTrans.ForgeDriftTeam || [];
    },
    teamRows() {
      const leftMembers = [];
      const rightMembers = [];

      this.members.forEach((member, index) => {
        if (index % 2 === 0) {
          leftMembers.push(member);
        } else {
          rightMembers.push(member);
        }
      });

      const rows = Math.max(leftMembers.length, rightMembers.length);

      return Array.from({ length: rows }, (_, index) => ({
        left: leftMembers[index],
        right: rightMembers[index],
      }));
    },
    teamHeading() {
      return (
        this.$store.state.trans.team_heading || "Meet the Team Behind Wizz Tech"
      );
    },
    teamIntro() {
      return (
        this.$store.state.trans.team_intro ||
        "Our team plans, builds, tests, and improves digital products together."
      );
    },
    teamDescription() {
      return (
        this.$store.state.trans.our_team_description ||
        "A multidisciplinary team combining AI, engineering, and product expertise to deliver scalable, real-world solutions."
      );
    },
  },
  methods: {
    getMemberImageUrl(member) {
      const memberImages = {
        "alishba ishaq": "alishba.png",
        "amna nazakat": "amna-nazakat.png",
        "naeem ullah": "naeem.png",
        "sajawal khan": "sajawal.png",
      };
      const matchedImage = memberImages[member?.Name?.toLowerCase()];

      if (matchedImage) {
        return this.getImageUrl(matchedImage);
      }

      const fileName = member?.ImagesUrl || "";
      const genericImages = ["male.png", "female.png"];

      if (!fileName || genericImages.includes(fileName.toLowerCase())) {
        return "";
      }

      return this.getImageUrl(fileName);
    },
    getLinkedInUrl(member) {
      const link = member?.LinkedIn?.trim();

      if (!link) return "";

      if (link.startsWith("http://") || link.startsWith("https://")) {
        return link;
      }

      return `https://${link}`;
    },
    handleLinkedInClick(event, member) {
      if (!this.getLinkedInUrl(member)) {
        event.preventDefault();
      }
    },
    getImageUrl(fileName) {
      if (!fileName) return "";

      try {
        return require(`@/assets/img/${fileName}`);
      } catch (error) {
        return "";
      }
    },
    getDepartment(member) {
      const position = (member?.Position || "").toLowerCase();

      if (
        position.includes("owner") ||
        position.includes("manager") ||
        position.includes("lead")
      ) {
        return "Leadership";
      }

      if (
        position.includes("marketing") ||
        position.includes("business") ||
        position.includes("strategy")
      ) {
        return "Business";
      }

      if (
        position.includes("designer") ||
        position.includes("product") ||
        position.includes("ui") ||
        position.includes("ux")
      ) {
        return "Product & Design";
      }

      if (
        position.includes("qa") ||
        position.includes("quality") ||
        position.includes("scrum")
      ) {
        return "Quality";
      }

      return "Engineering";
    },
  },
};
</script>

<style>
.our-team {
  padding: 0 18px 54px;
  background: radial-gradient(
      circle at 18% 24%,
      rgba(84, 172, 177, 0.12),
      transparent 28%
    ),
    radial-gradient(
      circle at 82% 72%,
      rgba(84, 172, 177, 0.12),
      transparent 30%
    ),
    linear-gradient(180deg, #ffffff 0%, #eef8f8 100%);
  color: #111827;
}

.team-divider {
  color: #f1a208;
  border-top: 2px solid #f1a208;
  opacity: unset;
  padding-bottom: 70px;
  width: 99%;
  margin: 0 auto;
}

.team-shell {
  width: min(976px, 100%);
  margin: 0 auto;
}

.team-header {
  text-align: center;
  margin-bottom: 8px;
}

.team-header h2 {
  margin: 0 0 16px;
  color: #5f0085;
  font-size: 48px;
  font-weight: 800;
  line-height: 1.12;
}

.team-header p {
  margin: 0;
  color: #344054;
  font-size: 16px;
  font-weight: 600;
}

.team-timeline {
  position: relative;
  display: grid;
  gap: 22px;
  padding: 108px 0 8px;
}

.timeline-line {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  width: 4px;
  background: #65008d;
  border-radius: 999px;
  transform: translateX(-50%);
}

.team-row {
  position: relative;
  display: grid;
  grid-template-columns: 360px 360px;
  justify-content: space-between;
  gap: 256px;
}

.team-row::before,
.team-row::after {
  content: "";
  position: absolute;
  top: 50%;
  width: 128px;
  height: 2px;
  background: #8b2bb2;
  transform: translateY(-50%);
}

.team-row::before {
  right: 50%;
}

.team-row::after {
  left: 50%;
}

.team-card,
.team-card-placeholder {
  min-height: 90px;
}

.team-card {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 54px minmax(0, 1fr) 24px;
  align-items: center;
  gap: 16px;
  padding: 17px 18px;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid rgba(120, 132, 148, 0.34);
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(16, 24, 40, 0.2);
}

.team-card-featured {
  border-color: rgba(95, 0, 133, 0.26);
  box-shadow: 0 0 0 2px rgba(95, 0, 133, 0.12), 0 2px 4px rgba(16, 24, 40, 0.2);
}

.member-avatar {
  width: 50px;
  height: 50px;
  display: grid;
  place-items: center;
  overflow: hidden;
  border-radius: 50%;
}

.member-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-fallback {
  width: 50px;
  height: 50px;
  position: relative;
  display: block;
  background: #ffffff;
  border: 2px solid #65008d;
  border-radius: 50%;
}

.avatar-fallback::before,
.avatar-fallback::after {
  content: "";
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  border: 2px solid #65008d;
}

.avatar-fallback::before {
  top: 13px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
}

.avatar-fallback::after {
  bottom: 11px;
  width: 20px;
  height: 13px;
  border-bottom: 0;
  border-radius: 14px 14px 0 0;
}

.member-copy {
  min-width: 0;
}

.member-copy h3 {
  margin: 0 0 5px;
  color: #101828;
  font-size: 15px;
  font-weight: 800;
  line-height: 1.2;
}

.member-copy p {
  margin: 0 0 5px;
  color: #344054;
  font-size: 12px;
  line-height: 1.25;
}

.member-copy span {
  display: block;
  color: #98a2b3;
  font-size: 12px;
  line-height: 1.2;
}

.linkedin-link {
  display: grid;
  width: 32px;
  height: 32px;
  place-items: center;
  text-decoration: none;
}

.linkedin-link img {
  width: 20px;
  height: 20px;
  object-fit: contain;
}

.linkedin-link-muted {
  cursor: default;
  opacity: 1;
}

.team-bottom {
  display: flex;
  justify-content: center;
  padding-top: 70px;
}

.team-bottom p {
  width: min(750px, 100%);
  margin: 0;
  color: #344054;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.55;
  text-align: center;
}

@media (max-width: 991px) {
  .team-header h2 {
    font-size: 38px;
  }

  .team-timeline {
    padding-top: 40px;
  }

  .team-row {
    grid-template-columns: 1fr;
    gap: 18px;
    padding-left: 28px;
  }

  .team-row::before,
  .team-row::after {
    display: none;
  }

  .timeline-line {
    left: 0;
  }

  .team-card-placeholder {
    display: none;
  }
}

@media (max-width: 575px) {
  .our-team {
    padding: 0 15px 48px;
  }

  .team-divider {
    width: 90%;
    padding-bottom: 48px;
  }

  .team-header h2 {
    font-size: 32px;
  }

  .team-header p,
  .team-bottom p {
    font-size: 14px;
  }

  .team-card {
    grid-template-columns: 48px minmax(0, 1fr) 22px;
    gap: 12px;
    padding: 15px;
  }

  .member-avatar,
  .avatar-fallback {
    width: 46px;
    height: 46px;
  }
}
</style>
